/**
 * Returning a visitor to /claim-organization/:slug after they sign up or sign
 * in from there, across the two round trips that leave the page:
 *
 * - Google OAuth: the provider owns its redirect URL, so the claim rides in the
 *   stored OAuth intent (`compaire_oauth_intent`, ten-minute TTL, consumed by
 *   /auth/callback), next to the invitation and review tokens.
 * - Email confirmation: the claim rides on the constant /auth/callback URL as
 *   two query parameters. Both hosted projects autoconfirm today, so this
 *   carrier is dormant; it exists so turning confirmation on does not drop
 *   the claim.
 *
 * Never an open redirect: no destination is ever read. Only a slug that
 * matches CLAIM_SLUG_RE and an allowlisted `from` value are carried, and the
 * path is rebuilt from a hardcoded template.
 */

export const CLAIM_SLUG_RE = /^[a-z0-9-]{1,100}$/;

/** `from` values the claim page may be opened with. */
export const CLAIM_FROM_VALUES = ["listing", "banner"] as const;

/** Query parameters on the /auth/callback URL (email-confirmation carrier). */
export const CLAIM_SLUG_PARAM = "claim_org";
export const CLAIM_FROM_PARAM = "claim_from";

export interface ClaimReturn {
  slug: string;
  from?: string | null;
}

const validFrom = (from: unknown): string | null =>
  typeof from === "string" && (CLAIM_FROM_VALUES as readonly string[]).includes(from) ? from : null;

/** A carried claim, re-validated, or null. */
export function validClaimReturn(value: unknown): ClaimReturn | null {
  if (!value || typeof value !== "object") return null;
  const { slug, from } = value as { slug?: unknown; from?: unknown };
  if (typeof slug !== "string" || !CLAIM_SLUG_RE.test(slug)) return null;
  const f = validFrom(from);
  return f ? { slug, from: f } : { slug };
}

/** The claim page path for a carried claim, or null when it is not valid. */
export function claimReturnPath(value: unknown): string | null {
  const claim = validClaimReturn(value);
  if (!claim) return null;
  const path = `/claim-organization/${claim.slug}`;
  return claim.from ? `${path}?from=${claim.from}` : path;
}

/** The claim the page was opened as (`useParams` slug and the page's query string). */
export function claimReturnFromPage(slug: string | undefined, search: string): ClaimReturn | null {
  return validClaimReturn({ slug, from: new URLSearchParams(search).get("from") });
}

/** `emailRedirectTo` for a sign-up started on the claim page. */
export function claimSignupRedirectUrl(origin: string, value: unknown): string {
  const callback = `${origin}/auth/callback`;
  const claim = validClaimReturn(value);
  if (!claim) return callback;
  const params = new URLSearchParams({ [CLAIM_SLUG_PARAM]: claim.slug });
  if (claim.from) params.set(CLAIM_FROM_PARAM, claim.from);
  return `${callback}?${params.toString()}`;
}

/** Where /auth/callback sends a visitor whose callback URL carries a claim, or null. */
export function claimCallbackRedirect(search: string): string | null {
  const params = new URLSearchParams(search);
  return claimReturnPath({ slug: params.get(CLAIM_SLUG_PARAM), from: params.get(CLAIM_FROM_PARAM) });
}
